import { Icon, Message } from 'semantic-ui-react';
import Dropzone from 'react-dropzone';
import React from 'react';
import styled from 'styled-components';
import AsyncPageContent from 'common/components/page/AsyncPageContent';
import { FileTypes } from 'common/constants/filetypes';
import { PageContent, PageHeader } from 'common/styles/page';
import { StyledAddButton, StyledCancelButton } from 'common/components/button/StyledButton';
import { useApiAdminV1RepositoryRepositoryFilesCreateMutation } from '../../../../store/generated/admin/repository-files';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from 'common/components/LoadingSpinner';
import {
  ProcessingStatusIndicator,
  ProcessingStatus,
  TranscriptionStatus,
} from './ProcessingStatusIndicator';

export const FileUpload = () => {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>();
  const [uploadedFiles, setUploadedFiles] = React.useState<File[]>([]);
  const [uploadedFileData, setUploadedFileData] = React.useState<any>(null);
  const [createFileUpload, { isLoading }] = useApiAdminV1RepositoryRepositoryFilesCreateMutation();

  const getFileType = (filename: string): number => {
    const extension = filename.split('.').pop()?.toLowerCase();
    if (extension === 'pdf') return FileTypes.PDF;
    if (extension === 'mp4') return FileTypes.MP4;
    return FileTypes.INVALID_FILE_TYPE;
  };

  const handleDrop = (acceptedFiles: File[]) => {
    setUploadedFiles(acceptedFiles);
  };

  const onClickSave = async () => {
    const fileType = getFileType(uploadedFiles[0].name);

    if (fileType === FileTypes.INVALID_FILE_TYPE) {
      setErrorMessage('Invalid file type. Please upload a PDF or MP4 file.');
      return;
    }

    try {
      const fileBlob = uploadedFiles[0];

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', fileBlob);
      formData.append('type', String(fileType));

      const response = await createFileUpload({ body: formData as any }).unwrap();
      setUploadedFileData(response);

      // Navigate to file details after a short delay to show processing status
      setTimeout(() => {
        navigate('/settings/admin/repository-management/file-details', {
          state: { data: response },
          replace: true,
        });
      }, 1000);
    } catch (error) {
      setErrorMessage('There was an error uploading the file. Please try again.');
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <AsyncPageContent isLoading={false} error={undefined}>
      <PageContent>
        <Wrapper>
          {isLoading && (
            <Overlay>
              <LoadingSpinner text="Uploading" />
            </Overlay>
          )}

          {errorMessage && (
            <Message
              negative
              onDismiss={() => setErrorMessage(undefined)}
              header="File Upload Error"
              content={errorMessage}
            />
          )}

          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <PageHeader title="Upload File Resource" />
          </div>

          <Dropzone onDrop={handleDrop} noKeyboard maxFiles={1} multiple={false}>
            {({ getRootProps, getInputProps }) => (
              <Box {...getRootProps()} file={uploadedFiles?.length > 0}>
                {uploadedFiles?.[0]?.name ? (
                  <InnerBox onClick={() => setUploadedFiles([])}>
                    {uploadedFiles?.[0]?.name} <Icon name="cancel" />
                  </InnerBox>
                ) : (
                  <div style={{ textAlign: 'center' }}>
                    <Icon name="file outline" size="huge" style={{ marginBottom: '1rem' }} />
                    <p style={{ fontSize: '1.2em', color: '#888' }}>
                      Drag & drop resource files here
                      <br />
                      or click to select files
                    </p>
                  </div>
                )}
                <input {...getInputProps()} />
              </Box>
            )}
          </Dropzone>

          <div
            style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '1rem' }}
          >
            <StyledCancelButton onClick={handleCancel} />
            <StyledAddButton
              onClick={onClickSave}
              label="Upload File"
              disabled={!uploadedFiles?.[0]?.name}
            />
          </div>

          {/* Show processing status after upload */}
          {uploadedFileData && (
            <div style={{ marginTop: '2rem' }}>
              <ProcessingStatusIndicator
                processingStatus={uploadedFileData.processing_status as ProcessingStatus}
                transcriptionStatus={uploadedFileData.transcription_status as TranscriptionStatus}
                processingError={uploadedFileData.processing_error}
                transcriptionError={uploadedFileData.transcription_error}
                fileType={uploadedFileData.type}
                fileId={uploadedFileData.id}
              />
            </div>
          )}
        </Wrapper>
      </PageContent>
    </AsyncPageContent>
  );
};

const Box = styled.div<{ file: boolean }>`
  border-radius: 10px;
  border: 2px dashed #ccc;
  background-color: #f9f9f9;
  width: 100%;
  max-width: 400px;
  aspect-ratio: 1 / 1; /* square */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 0 auto 30px auto;
  cursor: ${({ file }) => (file ? 'default' : 'pointer')};
  padding: 2rem;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f0f0f0;
  }
`;

const InnerBox = styled.div`
  border-radius: 5px;
  border: 1px solid #e9e9e9;
  display: flex;
  align-items: center;
  margin: 10px;
  padding: 15px;
  cursor: pointer;
  background-color: #dcdcdc;
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
`;

const Wrapper = styled.div`
  position: relative;
`;
